// @flow

import React, { type Node, useCallback, useMemo } from 'react';
import { useUniqueId, useMustShow } from '@tectonics/hooks';
import { normalizeColombianDocument, type ColombianDocumentType } from '@tectonics/prs/colombia';
import { InputMask, formatValueWithMask } from '@tectonics/components/input-mask';
import { useUserInputAsTextFieldWithFormatting, useUserInputValue } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';

import { TextField, UnderLabel } from '../../../../atoms';
import { ErrorLabel } from '../../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

export function FieldColombianDocument(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();
  const mustShow = useMustShow('colombianDocument');

  const [documentType] = useUserInputValue<ColombianDocumentType>('colombianDocumentType');

  const mask = useMemo(() => {
    return documentType === 'CE'
      ? CE_MASK
      : CC_NIT_MASK;
  }, [documentType]);

  const formatRaw = useCallback((rawValue: string) => {
    return formatValueWithMask(rawValue, mask);
  }, [mask]);

  const parseFormatted = useCallback((formattedValue: string) => {
    return normalizeColombianDocument(documentType, formattedValue);
  }, [documentType]);

  const [document, handleChangeDocument] = useUserInputAsTextFieldWithFormatting(
    'colombianDocument',
    formatRaw,
    parseFormatted,
  );

  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('colombianDocument');

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('document');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  if(!mustShow)
    return null;

  return (
    <div data-test-name="customer-document">
      <label htmlFor={inputId}>Numero del documento</label>

      <TextField
        as={InputMask}
        id={inputId}
        type="tel"
        value={document}
        onChange={handleChangeDocument}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        mask={mask}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="customer-document"
      />

      {formHasBeenSent && validationError && (
        <ValidationErrorMessage validationError={validationError} />
      )}
      {documentType === 'NIT' && (
        <UnderLabel>
          Llenar el número de su documento completo, incluyendo el dígito verificador de su NIT
        </UnderLabel>
      )}
    </div>
  );
}

type ValidationErrorMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return <ErrorLabel>El documento es requerido</ErrorLabel>;
    case 'invalid-pattern':
    case 'invalid-colombian-document-pattern':
      return <ErrorLabel>El documento informado es inválido</ErrorLabel>;

    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return <ErrorLabel>El documento informado es inválido</ErrorLabel>;
  }
}

const CE_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const CC_NIT_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];
